import {
  PopoverClose,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
  Root,
} from "@radix-ui/react-popover";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { authApi } from "services/auth";
import { Button } from "ui-kit/Button";
import { wagmiConfig } from "configurations/wagmi";
import { useAccount, useDisconnect } from "wagmi";

interface WalletPopoverProps {
  children?: ReactNode;
}

export function WalletPopover({ children }: WalletPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { connector } = useAccount();
  const { disconnectAsync } = useDisconnect({ config: wagmiConfig });
  const { t } = useTranslation();

  const onClickDisconnectHandler = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.warn("Failed to logout:", error);
    } finally {
      try {
        if (connector) {
          await disconnectAsync({ connector });
        } else {
          await disconnectAsync();
        }
      } catch (disconnectError) {
        console.warn("Failed to disconnect wallet:", disconnectError);

        if (connector && typeof connector.disconnect === "function") {
          try {
            await connector.disconnect();
          } catch (manualDisconnectError) {
            console.warn(
              "Manual connector disconnect failed:",
              manualDisconnectError,
            );
          }
        }
      } finally {
        setIsOpen(false);
      }
    }
  };

  return (
    <Root open={isOpen} onOpenChange={(val) => setIsOpen(val)}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <AnimatePresence>
        {isOpen && (
          <PopoverPortal forceMount>
            <PopoverContent sideOffset={8} asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="relative w-full !z-[2070] bg-white p-2 rounded-full shadow-lg"
              >
                <PopoverClose asChild>
                  <Button
                    className="px-[16px] py-[5px] h-auto text-[15px] w-full"
                    onClick={onClickDisconnectHandler}
                  >
                    {t("header.walletPopover.disconnect")}
                  </Button>
                </PopoverClose>
              </motion.div>
            </PopoverContent>
          </PopoverPortal>
        )}
      </AnimatePresence>
    </Root>
  );
}
